// Kick, YouTube and TikTok rows from the relay, finished in the page the way
// the app finishes them in Rust: links cut out of the text (text_spans.rs), and
// on Kick and YouTube the channel's 7TV emotes (providers/kick_emotes.rs,
// youtube_emotes.rs). The relay names each row's 7TV owner in the `sn-7tv` tag
// (`KICK:<user id>` or `GOOGLE:<channel id>`); the page loads that channel's
// table, keeps it live and repairs rows drawn before it landed, through the same
// machinery as Twitch (roomEmotes, emoteRepair). BetterTTV and FFZ do not
// support Kick or YouTube, so 7TV is the whole table, as in the app.
//
// A channel seen for the first time (nothing saved from last time) holds its
// own rows until its table answers, at most EMOTE_HOLD_MS, so they show with
// their emotes instead of changing a moment after they appear. Every other
// channel keeps flowing, and moderation reaches the rows that wait.

import { applyEmotes, emoteScope, EMOTE_HOLD_MS, type SevenTvPlatform } from './emoteMap';
import { applyLinks } from './linkDetect';
import { acquireRoomEmotes, type RoomEmotes } from './roomEmotes';
import { createRepairer, type SegmentUpdate } from './emoteRepair';
import type { MessageSegment, OverlayBaseMessage } from './overlayMessage';

export const SEVENTV_OWNER_TAG = 'sn-7tv';

/** Channel tables held at once; an overlay has a few channels. */
const MAX_RELAY_TABLES = 8;

const OWNER_PATTERN = /^(KICK):(\d{1,12})$|^(GOOGLE):(UC[A-Za-z0-9_-]{22})$/;

/** The 7TV channel a relay row belongs to, or null. Anything not exactly a
 *  Kick user id or a YouTube channel id is ignored: it becomes part of a URL. */
export function sevenTvOwnerOf(
  tags: Readonly<Record<string, string | undefined>> | undefined,
): { platform: SevenTvPlatform; id: string } | null {
  const raw = tags?.[SEVENTV_OWNER_TAG];
  if (typeof raw !== 'string') return null;
  const m = OWNER_PATTERN.exec(raw);
  if (!m) return null;
  return m[1] ? { platform: 'KICK', id: m[2] } : { platform: 'GOOGLE', id: m[4] };
}

/** A relay frame: a chat row, or a moderation frame (`type: 'mod'`). */
type Frame = object;

const isMod = (frame: Frame) => (frame as { type?: unknown }).type === 'mod';

/** A moderation frame from the relay (Kick, YouTube and TikTok deletions). */
interface ModFrame {
  type: 'mod';
  action?: string;
  target?: string;
  provider?: string;
  channel?: string;
}

interface Table {
  room: RoomEmotes;
  /** Its table has answered (or its wait ran out): rows go straight through. */
  settled: boolean;
  /** Its rows waiting for that table, the first time the channel is seen. */
  held: OverlayBaseMessage[] | null;
}

/**
 * Finish relay frames in order. `onFrame` gets each frame ready to use (rows
 * with links and emotes); `onUpdate` hears rows already handed over whose
 * emotes were mended. `dispose` lets every table go.
 */
export function createRelayRows(handlers: { onFrame: (frame: Frame) => void; onUpdate: (updates: SegmentUpdate[]) => void }) {
  const tables = new Map<string, Table>();
  const repairer = createRepairer(handlers.onUpdate);
  let disposed = false;

  const finish = (m: OverlayBaseMessage): OverlayBaseMessage => {
    if (!m.segments || m.segments.length === 0) return m;
    const base = applyLinks(m.segments);
    const owner = sevenTvOwnerOf(m.tags);
    if (!owner) return { ...m, segments: base };
    const { room } = tableFor(owner.platform, owner.id);
    // No personal emotes: 7TV keys them by Twitch id, and a Kick or YouTube id
    // from the same number range would hand over a stranger's.
    const withEmotes = (segs: MessageSegment[]) => applyEmotes(segs, room.map);
    const segments = withEmotes(base);
    if (m.id) {
      repairer.keep({
        id: m.id,
        scopes: [emoteScope(owner.platform, owner.id)],
        base,
        shown: segments,
        rebuild: withEmotes,
        extend: withEmotes,
      });
    }
    return { ...m, segments };
  };

  /** Hand over a channel's waiting rows, in order; from then on it never waits. */
  const release = (table: Table) => {
    const rows = table.held;
    if (!rows) return;
    table.held = null;
    table.settled = true;
    if (!disposed) for (const row of rows) handlers.onFrame(finish(row));
  };

  const tableFor = (platform: SevenTvPlatform, id: string): Table => {
    const key = emoteScope(platform, id);
    const known = tables.get(key);
    if (known) {
      tables.delete(key);
      tables.set(key, known);
      return known;
    }
    const table: Table = { room: acquireRoomEmotes(platform, id), settled: false, held: null };
    tables.set(key, table);
    void table.room.ready.then(() => release(table));
    if (tables.size > MAX_RELAY_TABLES) {
      const [oldest, gone] = tables.entries().next().value as [string, Table];
      tables.delete(oldest);
      release(gone);
      gone.room.release();
    }
    return table;
  };

  /** Moderation also reaches rows still waiting for their table. */
  const dropHeld = (mod: ModFrame) => {
    const matches = (m: OverlayBaseMessage) =>
      mod.action === 'delete_msg'
        ? m.id === mod.target
        : mod.action === 'delete_user'
          ? m.user_id === mod.target && (m.provider ?? '') === mod.provider
          : mod.action === 'clear'
            ? (m.provider ?? '') === mod.provider && (!mod.channel || (m.channel ?? '').toLowerCase() === mod.channel.toLowerCase())
            : false;
    for (const t of tables.values()) if (t.held) t.held = t.held.filter((m) => !matches(m));
  };

  const deliver = (frame: Frame) => {
    if (isMod(frame)) {
      dropHeld(frame as ModFrame);
      handlers.onFrame(frame);
      return;
    }
    const row = frame as OverlayBaseMessage;
    const owner = sevenTvOwnerOf(row.tags);
    if (!owner) {
      handlers.onFrame(finish(row));
      return;
    }
    const table = tableFor(owner.platform, owner.id);
    if (table.held) {
      table.held.push(row);
      return;
    }
    if (!table.settled && !table.room.map.warm) {
      // A channel seen for the first time: only its rows wait for its table
      // (at most EMOTE_HOLD_MS), so every other channel keeps flowing.
      table.held = [row];
      setTimeout(() => release(table), EMOTE_HOLD_MS);
      return;
    }
    handlers.onFrame(finish(row));
  };

  return {
    handle(frame: Frame) {
      if (!disposed) deliver(frame);
    },
    dispose() {
      disposed = true;
      repairer.dispose();
      for (const t of tables.values()) {
        t.held = null;
        t.room.release();
      }
      tables.clear();
    },
  };
}
